/*
 * The AI chats reducer: one pure function from (model, event) to a new model and a verdict, over the
 * module's own `aichat.*` events. Core's `applyEvent` has already gated a durable event by `eventId`
 * before a case here runs, so no case repeats the catch-up rules; what a case does repeat is the
 * module's own order rule (S03 §1): of two rows for one chat the higher `rev` wins, of two read states
 * the higher `version`, so an event that arrives after the answer of the same write changes nothing.
 *
 * What it keeps is the slice's list facts (`sync/slice.ts`) — a chat's platform lines among them,
 * whose words the lists draw — and nothing of a conversation. An item's head, a revision, a landing,
 * the pictures and the terminal's screen are **applied without a change here**: the page that shows
 * that chat hears them itself (`sync/chatSession.ts`, `live/liveStore.ts`, `terminal/feed.ts`) and
 * fetches what it needs by rev, and a tab that is not on that page pays nothing. The one fact an item head does leave
 * in the slice is the chat's item rev, so a page that opens later starts from a rev at least that
 * new.
 *
 * An event of this module with nothing to do is still **applied**: the verdict is what moves
 * `meta.lastEventId`, so answering "unknown" for a durable event this build knows would have the
 * server deliver it again after every reconnection. "Unknown" is kept for what it means — a type from
 * a newer server.
 */

import type { EventReducer } from "../../core/modules/registry.ts"
import type { Model } from "../../core/store/model.ts"
import { isAichatEventType, type AichatEvent } from "./events.ts"
import {
	aichatSlice,
	withAichatSlice,
	withLoginUsage,
	withoutChat,
	withPreview,
	withRow,
	type AichatSlice,
} from "./sync/slice.ts"
import type { ItemHead, ReaderState } from "./wire/aichat.gen.ts"

/** The slice with one chat's item rev moved up to an item head's, where the slice knows the chat. */
function withItemRev(slice: AichatSlice, head: ItemHead): AichatSlice {
	const row = slice.chats[head.chatId]
	if (row === undefined || row.itemRev >= head.rev) return slice
	return { ...slice, chats: { ...slice.chats, [head.chatId]: { ...row, itemRev: head.rev } } }
}

/** One read state, kept only when it is newer than the one the slice holds. */
function withReader(slice: AichatSlice, reader: ReaderState): AichatSlice {
	const held = slice.readers[reader.chatId]
	if (held !== undefined && held.version > reader.version) return slice
	return { ...slice, readers: { ...slice.readers, [reader.chatId]: reader } }
}

/** The slice after one event of this module. Exhaustive: a new type is a compile error here. */
function next(slice: AichatSlice, event: AichatEvent): AichatSlice {
	switch (event.type) {
		case "aichat.chat.changed":
			return withRow(slice, event.data)
		case "aichat.chat.gone":
			return withoutChat(slice, event.data.chatId)
		case "aichat.item.new":
		case "aichat.item.changed":
			return withItemRev(slice, event.data)
		case "aichat.reader.state":
			return withReader(slice, event.data)
		case "aichat.login.changed":
			// One login's usage by its id; the newer picture wins, so a read that brought a later one
			// is never undone by an event replayed behind it.
			return withLoginUsage(slice, event.data)
		case "aichat.machine.changed":
			return { ...slice, machine: event.data }
		case "aichat.preview.changed":
			return withPreview(slice, event.data)
		case "aichat.revision.new":
		case "aichat.landing.changed":
		case "aichat.access.changed":
		case "aichat.live":
		case "aichat.workflow":
		case "aichat.agent":
		case "aichat.typing":
		case "aichat.viewers":
		case "aichat.terminal.output":
			return slice
	}
}

export const aichatReducer: EventReducer = (model: Model, event) => {
	if (!isAichatEventType(event.type)) return { next: model, result: "unknown" }
	const slice = aichatSlice(model)
	const after = next(slice, event as AichatEvent)
	return { next: after === slice ? model : withAichatSlice(model, after), result: "applied" }
}
